import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/store/auth";
import { useMobile } from "@/mobile/hooks/useMobile";
import { DialogHost } from "@/components/ui/confirm";

// Desktop pages
import AdminLayout from "@/layouts/AdminLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Tasks from "@/pages/Tasks";
import RecurringTasks from "@/pages/RecurringTasks";
import Approvals from "@/pages/Approvals";
import Workflows from "@/pages/Workflows";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import Departments from "@/pages/Departments";
import Teams from "@/pages/Teams";
import TaskTypes from "@/pages/TaskTypes";
import Priorities from "@/pages/Priorities";
import HolidayCalendar from "@/pages/HolidayCalendar";
import EscalationRules from "@/pages/EscalationRules";
import NumberingSeries from "@/pages/NumberingSeries";
import WhatsAppTemplates from "@/pages/WhatsAppTemplates";
import MessageLogs from "@/pages/MessageLogs";
import Memory from "@/pages/Memory";
import MemoryChat from "@/pages/MemoryChat";
import Reports from "@/pages/Reports";
import UserPerformance from "@/pages/UserPerformance";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";

// Mobile pages
import MobileLayout from "@/mobile/MobileLayout";
import MobileDashboard from "@/mobile/pages/MobileDashboard";
import MobileTasks from "@/mobile/pages/MobileTasks";
import MobileApprovals from "@/mobile/pages/MobileApprovals";
import MobileMemory from "@/mobile/pages/MobileMemory";
import MobileProfile from "@/mobile/pages/MobileProfile";
import MobileReports from "@/mobile/pages/MobileReports";
import MobileAudit from "@/mobile/pages/MobileAudit";
import MobileWhatsApp from "@/mobile/pages/MobileWhatsApp";

import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const isMobile = useMobile();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {isMobile ? (
        /* ── MOBILE ROUTES ────────────────────────────────────────
           MobileLayout handles its own auth guard.
           Masters, Settings, WABA config are desktop-only.
        ───────────────────────────────────────────────────────── */
        <>
          <Route path="/m" element={<MobileLayout />}>
            <Route index element={<MobileDashboard />} />
            <Route path="tasks" element={<MobileTasks />} />
            <Route path="inbox" element={<MobileApprovals />} />
            <Route path="ai" element={<MobileMemory />} />
            <Route path="me" element={<MobileProfile />} />
            <Route path="reports" element={<MobileReports />} />
            <Route path="audit" element={<MobileAudit />} />
            <Route path="whatsapp" element={<MobileWhatsApp />} />
          </Route>
          {/* Redirect any non-mobile route to mobile home */}
          <Route path="*" element={<Navigate to="/m" replace />} />
        </>
      ) : (
        /* ── DESKTOP ROUTES ───────────────────────────────────────
           Full ERP with all masters, settings, WABA config etc.
        ───────────────────────────────────────────────────────── */
        <>
          <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="recurring" element={<RecurringTasks />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="users" element={<Users />} />
            <Route path="roles" element={<Roles />} />
            <Route path="departments" element={<Departments />} />
            <Route path="teams" element={<Teams />} />
            <Route path="task-types" element={<TaskTypes />} />
            <Route path="priorities" element={<Priorities />} />
            <Route path="holidays" element={<HolidayCalendar />} />
            <Route path="escalation-rules" element={<EscalationRules />} />
            <Route path="numbering-series" element={<NumberingSeries />} />
            <Route path="whatsapp/templates" element={<WhatsAppTemplates />} />
            <Route path="whatsapp/incoming" element={<MessageLogs direction="incoming" />} />
            <Route path="whatsapp/outgoing" element={<MessageLogs direction="outgoing" />} />
            <Route path="memory" element={<Memory />} />
            <Route path="memory/chat" element={<MemoryChat />} />
            <Route path="reports" element={<Reports />} />
            <Route path="user-performance" element={<UserPerformance />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          {/* Mobile URL prefix on desktop → back to desktop home */}
          <Route path="/m/*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <DialogHost />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
